"use client";

import React, { useMemo, useState, type ComponentType } from "react";
import { InfoCircleOutlined } from "@ant-design/icons";
import {
  BarChart,
  BarList,
  Card,
  DonutChart,
  LineChart,
  Title,
  type CustomTooltipProps,
  type EventProps,
} from "@tremor/react";
import { Button, Empty, Segmented, Skeleton, Tooltip } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import sortBy from "lodash.sortby";

import { getOrderDiscountBreakdown, getOrderRevenueBreakdown } from "@fitness-app/data-models/entities/Order";
import { type ProductPrice } from "@fitness-app/data-models/entities/Product";
import { cn } from "@fitness-app/utils/src/styles/cn";

import { aggregateByPaymentMethod } from "~/modules/Products/Product/ProductDetails/components/paymentMethods";
import { aggregateSalesPerPrice } from "~/modules/Products/Product/ProductDetails/components/salesPerPrice";
import { useArchivedProductClients } from "~/modules/Products/Product/ProductDetails/hooks/useArchivedProductClients";
import { useCheckoutVariantOrders } from "~/modules/Products/Product/ProductDetails/hooks/useCheckoutVariantOrders";
import { getPriceFormatter } from "~/modules/Products/utils/getPriceFormatter";

interface ProductCheckoutVariantStatsAggregatedProps {
  checkoutVariantIds: string[];
  prices: ProductPrice[];
  productId: string;
  dateRange: [Dayjs, Dayjs] | null;
}

type OrderFilterType = "all" | "new" | "renewals";
type DiscountFilterType = "all" | "withoutDiscount" | "withDiscount" | "free";
type PaymentMethodMeasure = "transactions" | "paid";

/** Long tails of legacy prices would push every chart below the fold; the rest is a click away. */
const SALES_PER_PRICE_PREVIEW_ROWS = 10;

const stackedChartColors = ["indigo", "cyan", "emerald", "amber", "red", "purple", "pink", "lime", "sky", "fuchsia"];

const getPriceLabel = (price: ProductPrice | undefined) => {
  if (!price) {
    return null;
  }

  if (price.customLabel) {
    return price.customLabel;
  }

  const promoConfig = price.config?.showAsPromo;
  if (promoConfig && typeof promoConfig === "object" && "promoTagText" in promoConfig && promoConfig.promoTagText) {
    return promoConfig.promoTagText;
  }

  if (price.unit_amount) {
    return getPriceFormatter(price.currency).format((price.unit_amount ?? 0) / 100);
  }

  return price.id;
};

const getPriceDisplayLabel = (price: ProductPrice | undefined, fallbackId: string) => {
  const label = getPriceLabel(price) ?? fallbackId;
  return `${label}${price?.currency ? ` (${price.currency.toUpperCase()})` : ""}`;
};

type PayloadItem = {
  category: string;
  value: number;
  index: string;
  color: string;
  type?: string;
  payload: {
    priceBreakdown?: Record<string, PriceBreakdownEntry>;
    [key: string]: unknown;
  };
  name: string;
};

type PriceBreakdownEntry = { total: number; free: number };

interface TransactionsTooltipProps {
  active: boolean | undefined;
  payload: PayloadItem[];
  label: string;
}

const TransactionsTooltip = ({ active, payload, label }: TransactionsTooltipProps) => {
  if (!active || !payload?.length) return null;

  const data = payload[0];
  const priceBreakdown = data?.payload?.priceBreakdown;

  return (
    <div
      className={cn(
        "rounded-md border text-sm shadow-md",
        "border-gray-200 dark:border-gray-800",
        "bg-white dark:bg-gray-950",
      )}
    >
      <div className={cn("border-b border-inherit px-4 py-2")}>
        <p className={cn("font-medium", "text-gray-900 dark:text-gray-50")}>{label}</p>
      </div>
      <div className={cn("space-y-1 px-4 py-2")}>
        <div className="flex items-center justify-between space-x-8">
          <p className={cn("whitespace-nowrap text-right tabular-nums", "text-gray-700 dark:text-gray-300")}>
            Łącznie transakcji
          </p>
          <p className={cn("whitespace-nowrap text-right font-medium tabular-nums", "text-gray-900 dark:text-gray-50")}>
            {data?.value ?? 0}
          </p>
        </div>
        {priceBreakdown && Object.keys(priceBreakdown).length > 0 && (
          <div className="mt-2 border-t border-gray-200 pt-2 dark:border-gray-700">
            <p className="mb-1 text-xs font-medium text-gray-500 dark:text-gray-400">Podział wg ceny:</p>
            {Object.entries(priceBreakdown)
              .filter(([, entry]) => entry.total > 0)
              .sort(([, a], [, b]) => b.total - a.total)
              .map(([priceLabel, entry]) => (
                <div key={priceLabel} className="flex items-start justify-between gap-4">
                  {/* Wrapped, not truncated: a truncated label leaves the reader unable to
                      tell which package the transactions belong to. */}
                  <p className={cn("max-w-[320px] break-words text-xs", "text-gray-500 dark:text-gray-400")}>
                    {priceLabel}
                    {entry.free > 0 ? (
                      <span className="text-amber-600 dark:text-amber-500"> · {entry.free} × 0 zł</span>
                    ) : null}
                  </p>
                  <p className={cn("whitespace-nowrap text-xs tabular-nums", "text-gray-700 dark:text-gray-300")}>
                    {entry.total}
                  </p>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

const orderFilterOptions = [
  { label: "Wszystkie", value: "all" },
  { label: "Nowe", value: "new" },
  { label: "Odnowienia", value: "renewals" },
];

const paymentMethodMeasureOptions: { label: string; value: PaymentMethodMeasure }[] = [
  { label: "Transakcje", value: "transactions" },
  { label: "Przychód", value: "paid" },
];

const discountFilterOptions = [
  { label: "Wszystkie", value: "all" },
  { label: "Bez rabatu", value: "withoutDiscount" },
  { label: "Z rabatem", value: "withDiscount" },
  { label: "Gratisy (0 zł)", value: "free" },
];

type DiscountSourceOrder = Parameters<typeof getOrderDiscountBreakdown>[0];

const matchesDiscountFilter = (order: DiscountSourceOrder, filterType: DiscountFilterType) => {
  if (filterType === "all") {
    return true;
  }

  const { hasDiscount, isFree } = getOrderDiscountBreakdown(order);

  if (filterType === "free") {
    return isFree;
  }

  return filterType === "withDiscount" ? hasDiscount : !hasDiscount;
};

const filterOrdersByType = <T extends { metadata?: Record<string, unknown> | null }>(
  orders: T[],
  filterType: OrderFilterType,
): T[] => {
  if (filterType === "all") {
    return orders;
  }

  return orders.filter((order) => {
    const isRenewal = order.metadata?.renewal === true;

    if (filterType === "renewals") {
      return isRenewal;
    }
    // filterType === "new"
    return !isRenewal;
  });
};

export const ProductCheckoutVariantStatsAggregated = ({
  checkoutVariantIds,
  prices,
  productId,
  dateRange,
}: ProductCheckoutVariantStatsAggregatedProps): React.ReactElement => {
  const { data: allPaidOrders, isLoading } = useCheckoutVariantOrders(checkoutVariantIds, productId, dateRange);
  const { data: archivedClients } = useArchivedProductClients(productId, dateRange);
  const [discountFilter, setDiscountFilter] = useState<DiscountFilterType>("all");
  const [showAllSalesPerPrice, setShowAllSalesPerPrice] = useState(false);
  const [salesPerPriceFilter, setSalesPerPriceFilter] = useState<OrderFilterType>("all");
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<OrderFilterType>("all");
  const [paymentMethodMeasure, setPaymentMethodMeasure] = useState<PaymentMethodMeasure>("transactions");
  const [donutActiveCategory, setDonutActiveCategory] = useState<string | null>(null);
  const [projectedDonutActiveCategory, setProjectedDonutActiveCategory] = useState<string | null>(null);

  // Filter states for each chart
  const [revenueFilter, setRevenueFilter] = useState<OrderFilterType>("all");
  const [projectedRevenueFilter, setProjectedRevenueFilter] = useState<OrderFilterType>("all");
  const [transactionsFilter, setTransactionsFilter] = useState<OrderFilterType>("all");
  const [dailyRevenueFilter, setDailyRevenueFilter] = useState<OrderFilterType>("all");

  // Every chart below reads the discount-scoped set, so "z rabatem" / "gratisy" is one
  // decision rather than a per-card switch that could be left inconsistent.
  const paidOrders = useMemo(
    () => (allPaidOrders ?? []).filter((order) => matchesDiscountFilter(order, discountFilter)),
    [allPaidOrders, discountFilter],
  );

  /**
   * What the codes did to the selected range. A 100% redemption is a paid order with
   * `price.amount = 0`: revenue already reflects it, the transaction count does not — which
   * is what made a giveaway read as a sale.
   */
  const discountSummary = useMemo(() => {
    const perCode = new Map<string, { transactions: number; revenue: number; discount: number; free: number }>();
    let withDiscount = 0;
    let free = 0;
    let discountTotal = 0;

    for (const order of allPaidOrders ?? []) {
      const breakdown = getOrderDiscountBreakdown(order);

      if (breakdown.isFree) {
        free += 1;
      }

      if (!breakdown.hasDiscount) {
        continue;
      }

      withDiscount += 1;
      discountTotal += breakdown.discountAmount;

      // An order without a code but below list price (manual correction, funnel price
      // override) still gave money away — it is grouped rather than dropped.
      const codes = breakdown.codes.length ? breakdown.codes : ["(bez kodu)"];

      for (const code of codes) {
        const current = perCode.get(code) ?? { transactions: 0, revenue: 0, discount: 0, free: 0 };
        perCode.set(code, {
          transactions: current.transactions + 1,
          revenue: current.revenue + breakdown.paid,
          // Stacked codes share one order: the value is attributed to each of them, so the
          // per-code column sums higher than the total when codes were combined.
          discount: current.discount + breakdown.discountAmount,
          free: current.free + (breakdown.isFree ? 1 : 0),
        });
      }
    }

    return {
      total: allPaidOrders?.length ?? 0,
      withDiscount,
      free,
      discountTotal,
      perCode: sortBy(
        Array.from(perCode.entries()).map(([code, stats]) => ({ code, ...stats })),
        (entry) => -entry.discount,
      ),
    };
  }, [allPaidOrders]);

  const variantPrices = useMemo(() => {
    return prices.reduce<Record<string, ProductPrice>>((acc, price) => {
      acc[price.id] = price;
      return acc;
    }, {});
  }, [prices]);

  // Reads the discount-scoped set like every chart below. The new/renewal split is both a column
  // and a filter: the columns answer "how many of which package" at a glance, the filter narrows
  // the table when only one kind matters.
  const salesPerPrice = useMemo(
    () =>
      aggregateSalesPerPrice(
        filterOrdersByType(paidOrders, salesPerPriceFilter),
        variantPrices,
        getPriceDisplayLabel,
        archivedClients ?? [],
      ),
    [paidOrders, variantPrices, salesPerPriceFilter, archivedClients],
  );
  const salesPerPriceFormatter = useMemo(() => getPriceFormatter(salesPerPrice.currency), [salesPerPrice.currency]);
  const paymentMethods = useMemo(
    () => aggregateByPaymentMethod(filterOrdersByType(paidOrders, paymentMethodFilter)),
    [paidOrders, paymentMethodFilter],
  );
  const paymentMethodBars = useMemo(
    () =>
      paymentMethods.map((row) => ({
        name: `${row.label} · ${row.transactions} ${row.transactions === 1 ? "transakcja" : "transakcji"}`,
        value: paymentMethodMeasure === "paid" ? row.paid / 100 : row.transactions,
      })),
    [paymentMethods, paymentMethodMeasure],
  );

  const visibleSalesPerPriceRows = showAllSalesPerPrice
    ? salesPerPrice.rows
    : salesPerPrice.rows.slice(0, SALES_PER_PRICE_PREVIEW_ROWS);
  const hiddenSalesPerPriceRows = salesPerPrice.rows.length - visibleSalesPerPriceRows.length;

  const revenuePerPrice = useMemo(() => {
    const filteredOrders = filterOrdersByType(paidOrders || [], revenueFilter);

    if (!filteredOrders.length) {
      return { actual: [], currency: "pln" };
    }

    const aggregated = filteredOrders.reduce<Record<string, { actual: number; currency: string }>>((acc, order) => {
      if (!order.priceId || !order.price || typeof order.price.amount !== "number") {
        return acc;
      }

      if (!variantPrices[order.priceId]) {
        return acc;
      }

      const amount = order.price.amount / 100;
      const currency = variantPrices[order.priceId]?.currency ?? "pln";

      const current = acc[order.priceId];
      acc[order.priceId] = {
        actual: (current?.actual ?? 0) + amount,
        currency,
      };
      return acc;
    }, {});

    const actual = sortBy(
      Object.entries(aggregated).map(([priceId, stats]) => {
        const price = variantPrices[priceId];
        const displayLabel = getPriceDisplayLabel(price, priceId);

        return {
          price: displayLabel,
          amount: stats.actual,
          currency: stats.currency,
        };
      }),
      (item) => -item.amount,
    );

    return { actual, currency: actual[0]?.currency ?? "pln" };
  }, [paidOrders, variantPrices, revenueFilter]);

  const projectedRevenuePerPrice = useMemo(() => {
    const filteredOrders = filterOrdersByType(paidOrders || [], projectedRevenueFilter);

    if (!filteredOrders.length) {
      return { projected: [], currency: "pln" };
    }

    const aggregated = filteredOrders.reduce<Record<string, { projected: number; currency: string }>>((acc, order) => {
      if (!order.priceId || !order.price || typeof order.price.amount !== "number") {
        return acc;
      }

      if (!variantPrices[order.priceId]) {
        return acc;
      }

      const currency = variantPrices[order.priceId]?.currency ?? "pln";
      const { projected } = getOrderRevenueBreakdown(order);

      const current = acc[order.priceId];
      acc[order.priceId] = {
        projected: (current?.projected ?? 0) + projected / 100,
        currency,
      };
      return acc;
    }, {});

    const projected = sortBy(
      Object.entries(aggregated).map(([priceId, stats]) => {
        const price = variantPrices[priceId];
        const displayLabel = getPriceDisplayLabel(price, priceId);

        return {
          price: displayLabel,
          amount: stats.projected,
          currency: stats.currency,
        };
      }),
      (item) => -item.amount,
    );

    return { projected, currency: projected[0]?.currency ?? "pln" };
  }, [paidOrders, variantPrices, projectedRevenueFilter]);

  const purchaseAmountPerPrice = revenuePerPrice.actual;
  const projectedAmountPerPrice = projectedRevenuePerPrice.projected;

  const purchaseAmountCurrency = revenuePerPrice.currency;
  const purchaseAmountValueFormatter = useMemo(
    () => getPriceFormatter(purchaseAmountCurrency),
    [purchaseAmountCurrency],
  );

  const projectedAmountCurrency = projectedRevenuePerPrice.currency;
  const projectedAmountValueFormatter = useMemo(
    () => getPriceFormatter(projectedAmountCurrency),
    [projectedAmountCurrency],
  );

  // Transactions per day - aggregated with price breakdown in tooltip data
  const transactionsPerDay = useMemo(() => {
    const filteredOrders = filterOrdersByType(paidOrders || [], transactionsFilter);

    if (!filteredOrders.length) {
      return { data: [] };
    }

    const aggregated = filteredOrders.reduce<
      Record<string, { total: number; free: number; priceBreakdown: Record<string, PriceBreakdownEntry> }>
    >((acc, order) => {
      if (!order.createdAt || !order.priceId) {
        return acc;
      }

      const price = variantPrices[order.priceId];
      const displayLabel = getPriceDisplayLabel(price, order.priceId);
      const dateKey = dayjs(order.createdAt).format("YYYY-MM-DD");
      const isFree = getOrderDiscountBreakdown(order).isFree;

      if (!acc[dateKey]) {
        acc[dateKey] = { total: 0, free: 0, priceBreakdown: {} };
      }

      const day = acc[dateKey];
      const entry = day.priceBreakdown[displayLabel] ?? { total: 0, free: 0 };

      day.total += 1;
      day.free += isFree ? 1 : 0;
      day.priceBreakdown[displayLabel] = { total: entry.total + 1, free: entry.free + (isFree ? 1 : 0) };
      return acc;
    }, {});

    const data = sortBy(
      Object.entries(aggregated).map(([isoDate, stats]) => ({
        isoDate,
        date: dayjs(isoDate).format("DD.MM.YYYY"),
        Transakcje: stats.total,
        priceBreakdown: stats.priceBreakdown,
      })),
      "isoDate",
    );

    return { data };
  }, [paidOrders, variantPrices, transactionsFilter]);

  // Revenue per day - aggregated total
  const revenuePerDay = useMemo(() => {
    const filteredOrders = filterOrdersByType(paidOrders || [], dailyRevenueFilter);

    if (!filteredOrders.length) {
      return {
        data: [],
        currency: "pln",
      };
    }

    let detectedCurrency: string | null = null;

    const aggregated = filteredOrders.reduce<Record<string, number>>((acc, order) => {
      if (!order.createdAt || !order.priceId || !order.price || typeof order.price.amount !== "number") {
        return acc;
      }

      const price = variantPrices[order.priceId];
      if (!price) {
        return acc;
      }

      detectedCurrency = detectedCurrency ?? price.currency ?? null;

      const isoDate = dayjs(order.createdAt).format("YYYY-MM-DD");
      acc[isoDate] = (acc[isoDate] ?? 0) + order.price.amount / 100;
      return acc;
    }, {});

    const data = sortBy(
      Object.entries(aggregated).map(([isoDate, amount]) => ({
        isoDate,
        date: dayjs(isoDate).format("DD.MM.YYYY"),
        Przychód: amount,
      })),
      "isoDate",
    );

    return {
      data,
      currency: detectedCurrency ?? purchaseAmountCurrency,
    };
  }, [paidOrders, variantPrices, purchaseAmountCurrency, dailyRevenueFilter]);

  const revenuePerDayValueFormatter = useMemo(
    () => getPriceFormatter(revenuePerDay.currency ?? "pln"),
    [revenuePerDay.currency],
  );

  const donutChartData = useMemo(
    () =>
      purchaseAmountPerPrice.map((item) => ({
        name: item.price,
        amount: item.amount,
      })),
    [purchaseAmountPerPrice],
  );

  const donutChartTotalValue = useMemo(
    () => donutChartData.reduce((sum, item) => sum + item.amount, 0),
    [donutChartData],
  );

  const donutActiveValue = donutActiveCategory
    ? donutChartData.find((item) => item.name === donutActiveCategory)?.amount
    : null;

  const donutDisplayedAmount = typeof donutActiveValue === "number" ? donutActiveValue : donutChartTotalValue;
  const donutDisplayedLabel = donutActiveCategory ?? "Łącznie";

  const handleDonutValueChange = (value: EventProps) => {
    if (!value || typeof value !== "object") {
      setDonutActiveCategory(null);
      return;
    }

    const category = "categoryClicked" in value ? value.categoryClicked : null;
    setDonutActiveCategory(category ?? null);
  };

  const projectedDonutChartData = useMemo(
    () =>
      projectedAmountPerPrice.map((item) => ({
        name: item.price,
        amount: item.amount,
      })),
    [projectedAmountPerPrice],
  );

  const projectedDonutChartTotalValue = useMemo(
    () => projectedDonutChartData.reduce((sum, item) => sum + item.amount, 0),
    [projectedDonutChartData],
  );

  const projectedDonutActiveValue = projectedDonutActiveCategory
    ? projectedDonutChartData.find((item) => item.name === projectedDonutActiveCategory)?.amount
    : null;

  const projectedDonutDisplayedAmount =
    typeof projectedDonutActiveValue === "number" ? projectedDonutActiveValue : projectedDonutChartTotalValue;
  const projectedDonutDisplayedLabel = projectedDonutActiveCategory ?? "Łącznie";

  const handleProjectedDonutValueChange = (value: EventProps) => {
    if (!value || typeof value !== "object") {
      setProjectedDonutActiveCategory(null);
      return;
    }

    const category = "categoryClicked" in value ? value.categoryClicked : null;
    setProjectedDonutActiveCategory(category ?? null);
  };

  if (isLoading) {
    return <Skeleton active paragraph={{ rows: 8 }} />;
  }

  const discountValueFormatter = getPriceFormatter(purchaseAmountCurrency);

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Title className="!mb-0">Rabaty</Title>
            <Tooltip title="Zamówienie z kodem 100% jest opłacone na 0 zł — przychód to widzi, licznik transakcji nie. Filtr obejmuje wszystkie wykresy poniżej.">
              <InfoCircleOutlined className="text-gray-400" />
            </Tooltip>
          </div>
          <Segmented
            options={discountFilterOptions}
            value={discountFilter}
            onChange={(value) => setDiscountFilter(value as DiscountFilterType)}
          />
        </div>

        <div className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <div>
            <p className="text-xs uppercase text-gray-500 dark:text-gray-400">Transakcje</p>
            <p className="text-lg font-semibold text-gray-900 dark:text-gray-50">{discountSummary.total}</p>
          </div>
          <div>
            <p className="text-xs uppercase text-gray-500 dark:text-gray-400">Z rabatem</p>
            <p className="text-lg font-semibold text-gray-900 dark:text-gray-50">
              {discountSummary.withDiscount}
              {discountSummary.total ? (
                <span className="ml-1 text-sm font-normal text-gray-500 dark:text-gray-400">
                  ({Math.round((discountSummary.withDiscount / discountSummary.total) * 100)}%)
                </span>
              ) : null}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase text-gray-500 dark:text-gray-400">Gratisy (0 zł)</p>
            <p className="text-lg font-semibold text-amber-600 dark:text-amber-500">{discountSummary.free}</p>
          </div>
          <div>
            <p className="text-xs uppercase text-gray-500 dark:text-gray-400">Wartość rabatów</p>
            <p className="text-lg font-semibold text-gray-900 dark:text-gray-50">
              {discountValueFormatter.format(discountSummary.discountTotal / 100)}
            </p>
          </div>
        </div>

        {discountSummary.perCode.length ? (
          <div className="mt-6 divide-y divide-gray-200 dark:divide-gray-800">
            <div className="grid grid-cols-4 gap-2 pb-2 text-xs uppercase text-gray-500 dark:text-gray-400">
              <p>Kod</p>
              <p className="text-right">Transakcje</p>
              <p className="text-right">Przychód</p>
              <p className="text-right">Wartość rabatu</p>
            </div>
            {discountSummary.perCode.map((entry) => (
              <div key={entry.code} className="grid grid-cols-4 gap-2 py-2 text-sm">
                <p className="break-words font-medium text-gray-900 dark:text-gray-50">
                  {entry.code}
                  {entry.free > 0 ? (
                    <span className="ml-1 text-xs font-normal text-amber-600 dark:text-amber-500">
                      {entry.free} × 0 zł
                    </span>
                  ) : null}
                </p>
                <p className="text-right tabular-nums text-gray-700 dark:text-gray-300">{entry.transactions}</p>
                <p className="text-right tabular-nums text-gray-700 dark:text-gray-300">
                  {discountValueFormatter.format(entry.revenue / 100)}
                </p>
                <p className="text-right tabular-nums text-gray-700 dark:text-gray-300">
                  {discountValueFormatter.format(entry.discount / 100)}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-6 text-sm text-gray-500 dark:text-gray-400">
            Żadne zamówienie w tym zakresie nie miało rabatu.
          </p>
        )}
      </Card>

      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Title className="!mb-0">Sprzedane pakiety</Title>
            <Tooltip title="Opłacone transakcje per cena w wybranym zakresie. Nowe = pierwszy zakup, odnowienia = kolejne cykle tego samego planu. Przychód to kwoty faktycznie pobrane, projekcja dolicza zaplanowane raty. Filtr rabatów powyżej obejmuje też tę tabelę.">
              <InfoCircleOutlined className="text-gray-400" />
            </Tooltip>
          </div>
          <Segmented
            options={orderFilterOptions}
            value={salesPerPriceFilter}
            onChange={(value) => setSalesPerPriceFilter(value as OrderFilterType)}
          />
        </div>

        {salesPerPrice.rows.length ? (
          <div className="mt-4 overflow-x-auto">
            <div className="min-w-[1120px] divide-y divide-gray-200 dark:divide-gray-800">
              <div className="grid grid-cols-[minmax(220px,2fr)_repeat(9,minmax(0,1fr))] gap-3 pb-2 text-xs uppercase text-gray-500 dark:text-gray-400">
                <p>Cena</p>
                <p className="text-right">Transakcje</p>
                <p className="text-right">Nowe</p>
                <p className="text-right">Odnowienia</p>
                <p className="text-right">Gratisy</p>
                <p className="text-right">Przychód</p>
                <p className="text-right">Projekcja</p>
                <p className="text-right">Śr. transakcja</p>
                <p className="text-right">Zwroty</p>
                <p className="text-right">Rezygnacje</p>
              </div>
              {visibleSalesPerPriceRows.map((row) => (
                <div
                  key={row.priceId}
                  className="grid grid-cols-[minmax(220px,2fr)_repeat(9,minmax(0,1fr))] items-center gap-3 py-2.5 text-sm"
                >
                  <div className="min-w-0">
                    <p className="break-words font-medium text-gray-900 dark:text-gray-50">{row.label}</p>
                    <div className="mt-1.5 flex items-center gap-2">
                      <div className="h-1.5 w-32 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
                        <div className="h-full rounded-full bg-indigo-500" style={{ width: `${row.share * 100}%` }} />
                      </div>
                      <span className="text-xs tabular-nums text-gray-500 dark:text-gray-400">
                        {Math.round(row.share * 100)}%
                      </span>
                    </div>
                  </div>
                  <p className="text-right text-base font-semibold tabular-nums text-gray-900 dark:text-gray-50">
                    {row.transactions}
                  </p>
                  <p className="text-right tabular-nums text-gray-700 dark:text-gray-300">{row.newPurchases}</p>
                  <p className="text-right tabular-nums text-gray-700 dark:text-gray-300">{row.renewals}</p>
                  <p
                    className={cn(
                      "text-right tabular-nums",
                      row.free ? "text-amber-600 dark:text-amber-500" : "text-gray-400 dark:text-gray-600",
                    )}
                  >
                    {row.free}
                  </p>
                  <p className="text-right tabular-nums text-gray-700 dark:text-gray-300">
                    {salesPerPriceFormatter.format(row.paid / 100)}
                  </p>
                  <p className="text-right tabular-nums text-gray-700 dark:text-gray-300">
                    {salesPerPriceFormatter.format(row.projected / 100)}
                  </p>
                  <p className="text-right tabular-nums text-gray-700 dark:text-gray-300">
                    {salesPerPriceFormatter.format(row.paid / row.transactions / 100)}
                  </p>
                  <p
                    className={cn(
                      "text-right tabular-nums",
                      row.refunds ? "text-rose-600" : "text-gray-400 dark:text-gray-600",
                    )}
                  >
                    {row.refunds}
                    {row.refunds ? (
                      <span className="block text-xs text-gray-500 dark:text-gray-400">
                        {salesPerPriceFormatter.format(row.refundedAmount / 100)}
                      </span>
                    ) : null}
                  </p>
                  <p
                    className={cn(
                      "text-right tabular-nums",
                      row.resignations ? "text-rose-600" : "text-gray-400 dark:text-gray-600",
                    )}
                  >
                    {row.resignations}
                    {row.resignationAvgDays !== null ? (
                      <span className="block text-xs text-gray-500 dark:text-gray-400">
                        śr. po {row.resignationAvgDays} dn.
                      </span>
                    ) : null}
                  </p>
                </div>
              ))}
              {hiddenSalesPerPriceRows > 0 || showAllSalesPerPrice ? (
                <div className="py-2 text-center">
                  <Button type="link" size="small" onClick={() => setShowAllSalesPerPrice((value) => !value)}>
                    {showAllSalesPerPrice
                      ? `Pokaż tylko ${SALES_PER_PRICE_PREVIEW_ROWS} najczęstszych`
                      : `Pokaż pozostałe (${hiddenSalesPerPriceRows})`}
                  </Button>
                </div>
              ) : null}
              <div className="grid grid-cols-[minmax(220px,2fr)_repeat(9,minmax(0,1fr))] gap-3 pt-3 text-sm font-semibold text-gray-900 dark:text-gray-50">
                <p>Razem</p>
                <p className="text-right tabular-nums">{salesPerPrice.totals.transactions}</p>
                <p className="text-right tabular-nums">{salesPerPrice.totals.newPurchases}</p>
                <p className="text-right tabular-nums">{salesPerPrice.totals.renewals}</p>
                <p className="text-right tabular-nums">{salesPerPrice.totals.free}</p>
                <p className="text-right tabular-nums">
                  {salesPerPriceFormatter.format(salesPerPrice.totals.paid / 100)}
                </p>
                <p className="text-right tabular-nums">
                  {salesPerPriceFormatter.format(salesPerPrice.totals.projected / 100)}
                </p>
                <p className="text-right tabular-nums">
                  {salesPerPriceFormatter.format(
                    salesPerPrice.totals.paid / Math.max(salesPerPrice.totals.transactions, 1) / 100,
                  )}
                </p>
                <p className="text-right tabular-nums">
                  {salesPerPrice.totals.refunds}
                  {salesPerPrice.totals.refunds ? (
                    <span className="block text-xs font-normal text-gray-500 dark:text-gray-400">
                      {salesPerPriceFormatter.format(salesPerPrice.totals.refundedAmount / 100)}
                    </span>
                  ) : null}
                </p>
                <p className="text-right tabular-nums">
                  {salesPerPrice.totals.resignations}
                  {salesPerPrice.totals.resignationAvgDays !== null ? (
                    <span className="block text-xs font-normal text-gray-500 dark:text-gray-400">
                      śr. po {salesPerPrice.totals.resignationAvgDays} dn.
                    </span>
                  ) : null}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Brak transakcji w tym zakresie" className="py-10" />
        )}
      </Card>

      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Title className="!mb-0">Metody płatności</Title>
            <Tooltip title="Czym klientki płaciły w wybranym zakresie. Karta (Manual) to karta potwierdzona przez sprzedawcę, bez automatycznego odnowienia.">
              <InfoCircleOutlined className="text-gray-400" />
            </Tooltip>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Segmented
              options={paymentMethodMeasureOptions}
              value={paymentMethodMeasure}
              onChange={(value) => setPaymentMethodMeasure(value as PaymentMethodMeasure)}
            />
            <Segmented
              options={orderFilterOptions}
              value={paymentMethodFilter}
              onChange={(value) => setPaymentMethodFilter(value as OrderFilterType)}
            />
          </div>
        </div>
        {paymentMethodBars.length ? (
          <BarList
            className="mt-6"
            data={paymentMethodBars}
            color="indigo"
            valueFormatter={(value: number) =>
              paymentMethodMeasure === "paid"
                ? salesPerPriceFormatter.format(value)
                : Intl.NumberFormat("pl-PL").format(value)
            }
          />
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Brak transakcji w tym zakresie" className="py-10" />
        )}
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 ">
        <Card>
          <div className="mb-4 flex items-center justify-between">
            <Title className="!mb-0">Przychód per cena</Title>
            <Segmented
              options={orderFilterOptions}
              value={revenueFilter}
              onChange={(value) => setRevenueFilter(value as OrderFilterType)}
            />
          </div>
          {purchaseAmountPerPrice.length ? (
            <div className="flex flex-col items-center">
              <p className="text-center text-sm text-gray-700 dark:text-gray-300">{donutDisplayedLabel}</p>
              <p className="mt-2 text-center text-2xl font-semibold text-gray-900 dark:text-gray-50">
                {purchaseAmountValueFormatter.format(donutDisplayedAmount ?? 0)}
              </p>
              <DonutChart
                data={donutChartData}
                category="amount"
                index="name"
                className="mx-auto mt-8 h-64 w-64"
                colors={stackedChartColors}
                valueFormatter={(value) => purchaseAmountValueFormatter.format(value ?? 0)}
                showLabel={false}
                onValueChange={handleDonutValueChange}
              />
            </div>
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Brak danych" className="py-10" />
          )}
        </Card>

        <Card>
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Title className="!mb-0">Projektowany przychód</Title>
              <Tooltip title="Pełna przewidywana wartość zamówień: pierwsza wpłata plus wszystkie zaplanowane raty, również te jeszcze niepobrane. Dla zamówień jednorazowych (bez rat) jest równa kwocie faktycznie zapłaconej.">
                <InfoCircleOutlined className="text-gray-400" />
              </Tooltip>
            </div>
            <Segmented
              options={orderFilterOptions}
              value={projectedRevenueFilter}
              onChange={(value) => setProjectedRevenueFilter(value as OrderFilterType)}
            />
          </div>
          {projectedAmountPerPrice.length ? (
            <div className="flex flex-col items-center">
              <p className="text-center text-sm text-gray-700 dark:text-gray-300">{projectedDonutDisplayedLabel}</p>
              <p className="mt-2 text-center text-2xl font-semibold text-gray-900 dark:text-gray-50">
                {projectedAmountValueFormatter.format(projectedDonutDisplayedAmount ?? 0)}
              </p>
              <DonutChart
                data={projectedDonutChartData}
                category="amount"
                index="name"
                className="mx-auto mt-8 h-64 w-64"
                colors={stackedChartColors}
                valueFormatter={(value) => projectedAmountValueFormatter.format(value ?? 0)}
                showLabel={false}
                onValueChange={handleProjectedDonutValueChange}
              />
            </div>
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Brak danych" className="py-10" />
          )}
        </Card>
      </div>

      <Card>
        <div className="mb-4 flex items-center justify-between">
          <Title className="!mb-0">Transakcje dziennie</Title>
          <Segmented
            options={orderFilterOptions}
            value={transactionsFilter}
            onChange={(value) => setTransactionsFilter(value as OrderFilterType)}
          />
        </div>
        {transactionsPerDay.data.length ? (
          <BarChart
            className="mt-6 h-72"
            data={transactionsPerDay.data}
            index="date"
            categories={["Transakcje"]}
            colors={["indigo"]}
            valueFormatter={(value) => (value != null ? Intl.NumberFormat("pl-PL").format(value) : "0")}
            showLegend={false}
            allowDecimals={false}
            showGridLines={true}
            showYAxis={true}
            yAxisWidth={60}
            customTooltip={TransactionsTooltip as unknown as ComponentType<CustomTooltipProps>}
          />
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Brak danych" className="py-10" />
        )}
      </Card>

      <Card>
        <div className="mb-4 flex items-center justify-between">
          <Title className="!mb-0">Przychód dziennie</Title>
          <Segmented
            options={orderFilterOptions}
            value={dailyRevenueFilter}
            onChange={(value) => setDailyRevenueFilter(value as OrderFilterType)}
          />
        </div>
        {revenuePerDay.data.length ? (
          <LineChart
            className="mt-6 h-80"
            data={revenuePerDay.data}
            index="date"
            categories={["Przychód"]}
            colors={["emerald"]}
            valueFormatter={(value) => revenuePerDayValueFormatter.format(value ?? 0)}
            showLegend={false}
            allowDecimals={false}
            showGridLines={true}
            showYAxis={true}
            yAxisWidth={100}
          />
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Brak danych" className="py-10" />
        )}
      </Card>
    </div>
  );
};
